import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Spinner, Stack, Text } from '@guestyci/arc';

import { utils } from 'utils';
import {
  fetchUserData,
  getSecureAuthCookie,
  setDataDogUser,
} from '../login/helpers';
import { saveUserPreferences } from 'utils/userPreferences';
import { clearLocalStorage, setToken, removeItem } from 'utils/localStorage';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Impersonation() {
  const { oktaAuth: authClient } = useOktaAuth();

  const [error, setError] = useState(undefined);

  const query = useQuery();
  const token = query.get('token');

  useEffect(() => {
    const handleImpersonation = async () => {
      clearLocalStorage();
      setToken(token);
      try {
        await getSecureAuthCookie();
        const user = await fetchUserData({ enrichUser: true });
        await setDataDogUser({ isImpersonated: true, user });

        await saveUserPreferences({
          accessToken: token,
          preferences: user?.preferences,
        });

        window.location.href = '/';
      } catch (e) {
        setError(e.message);
      }
    };

    if (token) {
      handleImpersonation();
    }
  }, [token]);

  useEffect(() => {
    const initAuthentication = async () => {
      try {
        // clear previous user data
        removeItem('token');
        const session = await authClient.session.get();
        if (session.status === 'INACTIVE') {
          throw new Error('Session inactive');
        }
        const { tokens } = await authClient.token.getWithoutPrompt();
        authClient.tokenManager.setTokens(tokens);
        const accessTokenObject = tokens?.accessToken;
        if (accessTokenObject) {
          utils.saveUserData(accessTokenObject?.accessToken);
        }
        await getSecureAuthCookie();
        const user = await fetchUserData({ enrichUser: true });
        const employeeEmail = accessTokenObject?.claims?.act;
        await setDataDogUser({
          isImpersonated: true,
          act: employeeEmail,
          user,
        });

        await saveUserPreferences({
          accessToken: accessTokenObject?.accessToken,
          preferences: user?.preferences,
        });

        window.location.replace('/');
      } catch (e) {
        console.error('Impersonation Okta session with error', e);
        setError(e);
      }
    };

    if (!token) {
      console.log('[Impersonation] Initializing authentication...');
      initAuthentication();
    }
  }, [authClient, token]);

  const sessionError = error?.message === 'Session inactive';

  const sessionCTA =
    'Session inactive. Please navigate to the admin page and login again.';

  return (
    <Stack
      spacing={0}
      align="center"
      justify="center"
      className="gst-min-h-screen gst-w-full"
    >
      {error ? (
        <Text className="gst-text-center">
          {sessionError ? sessionCTA : error?.message}
        </Text>
      ) : (
        <Spinner size="xl" className="gst-text-primary" />
      )}
    </Stack>
  );
}
